import React, { useEffect, useState } from "react";
import { Grid, Box, Button } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { OutletDetailsType } from "../../../features/createSecondaryOrder/CreateSecondaryOrderType";
import {
    CustomDateField,
    CustomAutocompleteField,
    CustomCostField,
    CustomTextAreaField,
    CustomFileUploadField,
    CustomNumberField,
    CustomBannerNameField
} from "./DisplayVisibilityCustomFields";
import { popupType } from "../../../types/types";
import { GenericPopUp } from "../../popup/genericPopUp";
import {AssetInstallationFormData} from "../../../features/claimsNew/utils/utils";
import { getConfigData } from "../../../services/configurationService";
import { formatDecimal } from "../../../components/dms_common/DmsUtils";

interface AssetInstallationFormProps {
    onSave: (data: AssetInstallationFormData, proceed: boolean) => void;
    initialData?: AssetInstallationFormData | null;
    customersInfo: OutletDetailsType[];
    currencySymbol: string;
    setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  }

const AssetInstallationForm: React.FC<AssetInstallationFormProps> = ({
    onSave,
    initialData = null,
    customersInfo,
    currencySymbol,
    setHasUnsavedChanges
}) => {
    const [formData, setFormData] = useState<AssetInstallationFormData>({
        id: Date.now().toString(),
        claimDate: dayjs(),
        outletCode: "",
        outletName: "",
        skuCode: "",
        skuName: "",
        quantity: 0,
        installationDate: dayjs(),
        installationCost: 0,
        displayCost: 0,
        remarks: "",
        blobUrls: [],
        totalClaimAmount: 0,
    });
    const [assetTypeOptions, setAssetTypeOptions] = useState<string[]>([]);
    const [genericModalState, setGenericModalState] = useState(false);
    const [genericModalMessage, setGenericModalSMessage] = useState("");
    const [genericModaltype, setGenericModalType] = useState<popupType>("Error");
    const [errors, setErrors] = useState<Record<string, boolean>>({
        outletCode: false,
        outletName: false,
        skuCode: false,
        skuName: false,
        quantity: false,
        installationDate: false, 
        installationCost: false,
        remarks: false,
        blobUrls: false,
    });
    
    const [errorMessages, setErrorMessages] = useState<Record<string, string>>({
        outletCode: "",
        outletName: "",
        skuCode: "",
        skuName: "",
        quantity:"",
        installationDate: "",
        installationCost: "",
        remarks: "",
        blobUrls: "",
    });
    const openPopUp = (message: string, modalType: popupType) => {
        setGenericModalSMessage(message);
        setGenericModalState(true);
        setGenericModalType(modalType);
    };

    const handleSkuCodeChange = (event: React.SyntheticEvent, value: string | null) => {
        setFormData((prev) => ({
            ...prev,
            skuCode: value || "",
        }));
    
        if (value) {
            setErrors((prev) => ({
                ...prev,
                skuCode: false,
            }));
            setErrorMessages((prev) => ({
                ...prev,
                skuCode: "",
            }));
        }
    };

    const handleOutletChange = (event: React.SyntheticEvent, value: OutletDetailsType | null) => {
        if (value) {
            setFormData((prev) => ({
                ...prev,
                outletCode: value.outletCode || "",
                outletName: value.outletName || "",
            }));
    
            setErrors((prev) => ({
                ...prev,
                outletCode: false,
                outletName: false,
            }));
            setErrorMessages((prev) => ({
                ...prev,
                outletCode: "",
                outletName: "",
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                outletCode: "",
                outletName: "",
            }));
        }
    };

    const handleDateChange = (name: keyof AssetInstallationFormData, newValue: Dayjs | null) => {
        const dateString = newValue ? newValue.format("DD-MM-YYYY") : "";
        const isValidFormat = validateDateFormat(dateString);
    
        if (!isValidFormat) {
            setErrors((prev) => ({ ...prev, [name]: true }));
            setErrorMessages((prev) => ({ ...prev, [name]: "Invalid date format." }));
        } else {
            setErrors((prev) => ({ ...prev, [name]: false }));
            setErrorMessages((prev) => ({ ...prev, [name]: "" }));
            setFormData({ ...formData, [name]: newValue });
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (value.trim() !== "") {
            setErrors((prev) => ({ ...prev, [name]: false }));
            setErrorMessages((prev) => ({ ...prev, [name]: "" }));
        }
    };
    
    const handleNumberInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        const numericValue = value === '' ? 0 : parseFloat(value);
        if (!isNaN(numericValue)) {
            setFormData((prev) => ({ ...prev, [name]: numericValue }));
            if (numericValue > 0) {
                setErrors((prev) => ({ ...prev, [name]: false }));
                setErrorMessages((prev) => ({ ...prev, [name]: "" }));
            }
        }
    };
    
    const handleCostInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        const numericValue = value === '' ? 0 : parseFloat(value);
        if (numericValue >= 0) {
            setFormData(prev => ({
                ...prev,
                [name]: numericValue,
                totalClaimAmount: prev.totalClaimAmount + numericValue - prev[name],
            }));
            if (numericValue > 0) {
                setErrors((prev) => ({ ...prev, [name]: false }));
                setErrorMessages((prev) => ({ ...prev, [name]: "" }));
            }
        }
        else {
            openPopUp("Negative numbers are not allowed.", "Alert");
            setFormData((prev) => ({
                ...prev,
                [name]: prev[name] || 0,
            }));
        }
    };

    const validateOutletCode = () => {
        const isValid = formData.outletCode !== "";
        setErrors((prev) => ({ ...prev, outletCode: !isValid }));
        setErrorMessages((prev) => ({ ...prev, outletCode: isValid ? "" : "Outlet Code is required" }));
        return isValid;
    };
    
    const validateOutletName = () => {
        const isValid = formData.outletName !== "";
        setErrors((prev) => ({ ...prev, outletName: !isValid }));
        setErrorMessages((prev) => ({ ...prev, outletName: isValid ? "" : "Outlet Name is required" }));
        return isValid;
    };
    
    const validateSkuCode = () => {
        const isValid = formData.skuCode !== "";
        setErrors((prev) => ({ ...prev, skuCode: !isValid }));
        setErrorMessages((prev) => ({ ...prev, skuCode: isValid ? "" : "Asset Type is required" }));
        return isValid;
    };
    
    const validateSkuName = () => {
        if (formData.skuName.trim() === "") {
            setErrors((prev) => ({ ...prev, skuName: true }));
            setErrorMessages((prev) => ({ ...prev, skuName: "Asset ID is required" }));
            return false;
        } else {
            setErrors((prev) => ({ ...prev, skuName: false }));
            setErrorMessages((prev) => ({ ...prev, skuName: "" }));
            return true;
        }
    };
    
    const validateQuantity = () => {
        if (formData.quantity <= 0) {
            setErrors((prev) => ({ ...prev, quantity: true }));
            setErrorMessages((prev) => ({ ...prev, quantity: "Quantity must be greater than 0" }));
            return false;
        } else {
            setErrors((prev) => ({ ...prev, quantity: false }));
            setErrorMessages((prev) => ({ ...prev, quantity: "" }));
            return true;
        }
    };
    
    const validateDateFormat = (date: string): boolean => {
        const regex = /^\d{2}-\d{2}-\d{4}$/;
        return regex.test(date);
    };

    const validateInstallationCost = () => {
        if (formData.installationCost <= 0) {
            setErrors((prev) => ({ ...prev, installationCost: true }));
            setErrorMessages((prev) => ({ ...prev, installationCost: "Installation Cost must be greater than 0" }));
            return false;
        } else {
            setErrors((prev) => ({ ...prev, installationCost: false }));
            setErrorMessages((prev) => ({ ...prev, installationCost: "" }));
            return true;
        }
    };
    
    const validateRemarks = () => {
        if (formData.remarks.trim() === "") {
            setErrors((prev) => ({ ...prev, remarks: true }));
            setErrorMessages((prev) => ({ ...prev, remarks: "Remarks are required" }));
            return false;
        } else {
            setErrors((prev) => ({ ...prev, remarks: false }));
            setErrorMessages((prev) => ({ ...prev, remarks: "" }));
            return true;
        }
    };
    
    const validateBlobUrls = () => {
        const isValid = formData.blobUrls.length > 0;
        setErrors((prev) => ({ ...prev, blobUrls: !isValid }));
        setErrorMessages((prev) => ({ ...prev, blobUrls: isValid ? "" : "Proof of Expense is required" }));
        return isValid;
    };

   const isFormValid = () => {
        return (
            formData.outletCode !== "" &&
            formData.outletName !== "" &&
            formData.skuCode !== "" &&           
            formData.skuName !== "" && 
            formData.quantity > 0 &&
            formData.installationCost > 0 &&
            formData.remarks !== "" &&
            formData.blobUrls.length > 0 &&
            !errors.installationDate
        );
    };

    const handleSave = (clearForm: boolean) => {
        const isOutletCodeValid = validateOutletCode();
        const isOutletNameValid = validateOutletName();
        const isSkuCodeValid = validateSkuCode();
        const isSkuNameValid = validateSkuName();
        const isQuantityValid = validateQuantity();
        const isInstallationCostValid = validateInstallationCost();
        const isRemarksValid = validateRemarks();
        const isBlobUrlsValid = validateBlobUrls();
    
        const isFormValid =
            isOutletCodeValid &&
            isOutletNameValid &&
            isSkuCodeValid &&
            isSkuNameValid &&
            isQuantityValid &&
            isInstallationCostValid &&
            isRemarksValid &&
            isBlobUrlsValid &&
            !errors.installationDate;
    
        if (!isFormValid) {
            return;
        }
        onSave(formData, !clearForm);
    
        if (clearForm) {
            setFormData({
                id: Date.now().toString(),
                claimDate: dayjs(),
                outletCode: "",
                outletName: "",
                skuCode: "",
                skuName: "",
                quantity: 0,
                installationDate: dayjs(),
                installationCost: 0,
                displayCost: 0,
                remarks: "",
                blobUrls: [],
                totalClaimAmount: 0,
            });
        }
        setHasUnsavedChanges(false);
    };

    useEffect(() => {
        if (initialData) {
            setFormData({
                id: initialData.id || "",
                claimDate: initialData.claimDate || dayjs(),
                outletCode: initialData.outletCode || "",
                outletName: initialData.outletName || "",
                skuCode: initialData.skuCode || "",
                skuName: initialData.skuName || "",
                quantity: initialData.quantity || 0,
                installationDate: initialData.installationDate || dayjs(),
                installationCost: initialData.installationCost || 0,
                displayCost: initialData.displayCost || 0,
                remarks: initialData.remarks || "",
                blobUrls: initialData.blobUrls || [],
                totalClaimAmount: initialData.totalClaimAmount || 0,
            });
        }
    }, [initialData]);

    useEffect(() => {
        const fetchRequiredConfigs = async () => {
          try {
            const assetTypeOptionsConfig = await getConfigData("dms_configuration", "assetTypeOptions");
            setAssetTypeOptions(assetTypeOptionsConfig?.[0]?.configValue ?? []);
          } catch (error) {
            console.error("Error fetching asset type options:", error);
          }
        };
        fetchRequiredConfigs();
      }, []);

      useEffect(() => {
        const isPartiallyFilled =
          formData.outletCode !== "" ||
          formData.outletName !== "" ||
          formData.skuCode !== "" ||
          formData.skuName !== "" ||
          formData.quantity > 0 ||
          formData.installationCost > 0 ||
          formData.displayCost > 0 ||
          formData.remarks !== "" ||
          formData.blobUrls.length > 0;
      
        setHasUnsavedChanges(isPartiallyFilled);
      }, [formData, setHasUnsavedChanges]);

    return (
        <Box
            sx={{
                padding: "26px",
                border: "1px solid #EBEBEB",
                borderRadius: 2,
                overflowY: "auto",
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <GenericPopUp
                type={genericModaltype}
                message={genericModalMessage}
                openGenericModal={genericModalState}
                setOpenGenericModal={() => {
                    setGenericModalState(false);
                }}
            />
            <Grid container spacing={2}>
                {/* Claim Date */}
                <CustomDateField
                    label="Claim Date"
                    value={formData.claimDate}
                    onChange={(newValue) => handleDateChange("claimDate", newValue)}
                    outerGridSize={12}
                    disabled
                />

                {/* Outlet Code */}
                <CustomAutocompleteField
                    label="Outlet Code"
                    options={customersInfo}
                    getOptionLabel={(option) => option.outletCode}
                    value={customersInfo.find((customer) => customer.outletCode === formData.outletCode) || null}
                    onChange={handleOutletChange}
                    required
                    placeholder="Select Outlet Code"
                    error={errors.outletCode}
                    helperText={errorMessages.outletCode}
                    onBlur={validateOutletCode}
                />

                <CustomAutocompleteField
                    label="Outlet Name"
                    options={customersInfo}
                    getOptionLabel={(option) => option.outletName}
                    value={customersInfo.find((customer) => customer.outletName === formData.outletName) || null}
                    onChange={handleOutletChange}
                    required
                    placeholder="Select Outlet Name"
                    error={errors.outletName}
                    helperText={errorMessages.outletName}
                    onBlur={validateOutletName}
                />

                <CustomAutocompleteField
                    label="Asset Type"
                    options={assetTypeOptions}
                    getOptionLabel={(option) => option}
                    value={formData.skuCode}
                    onChange={handleSkuCodeChange}
                    placeholder="Enter Asset Type"
                    required
                    error={errors.skuCode}
                    helperText={errorMessages.skuCode}
                    onBlur={validateSkuCode}
                />

                <CustomBannerNameField
                    label="Asset ID"
                    name="skuName"
                    value={formData.skuName}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter Asset ID"
                    error={errors.skuName}
                    helperText={errorMessages.skuName}
                    onBlur={validateSkuName}
                    onKeyDown={validateSkuName}
                />

                <CustomNumberField
                    label="Quantity"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleNumberInputChange}
                    required
                    placeholder="0"
                    error={errors.quantity}
                    helperText={errorMessages.quantity}
                    onBlur={validateQuantity}
                    onKeyDown={validateQuantity}
                />


                  {/* Installation Date */}
                  <CustomDateField
                      label="Installation Date"
                      value={formData.installationDate}
                      onChange={(newValue) => handleDateChange("installationDate", newValue)}
                      required
                      error={errors.installationDate}
                      helperText={errorMessages.installationDate}
                      onBlur={() => {
                          if (!formData.installationDate) {
                              setErrors((prev) => ({ ...prev, installationDate: true }));
                              setErrorMessages((prev) => ({ ...prev, installationDate: "Installation Date is required" }));
                          }
                      }}
                  />

                {/* Installation Cost */}
                <CustomCostField
                    label="Installation Cost"
                    name="installationCost"
                    value={formData.installationCost}
                    currencySymbol={currencySymbol}
                    onChange={handleCostInputChange}
                    required
                    placeholder="0"
                    error={errors.installationCost}
                    helperText={errorMessages.installationCost}
                    onBlur={validateInstallationCost}
                    onKeyDown={validateInstallationCost}
                />

                {/* Asset Value */}
                <CustomCostField
                    label="Asset Value"
                    name="displayCost"
                    value={formData.displayCost}
                    currencySymbol={currencySymbol}
                    onChange={handleCostInputChange}
                    placeholder="0"
                />

                {/* Remarks */}
                <CustomTextAreaField
                    label="Remarks"
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleInputChange}
                    required
                    outerGridSize={12}
                    placeholder="Write Remark..."
                    error={errors.remarks}
                    helperText={errorMessages.remarks}
                    onBlur={validateRemarks}
                    onKeyDown={validateRemarks}
                />

                <CustomFileUploadField
                    onFileChange={(blobUrls) => setFormData((prev) => ({ ...prev, blobUrls }))}
                    value={formData.proofOfExpenseImage}
                    openPopup={openPopUp}
                    blobUrls={formData.blobUrls}
                    error={errors.blobUrls}
                    helperText={errorMessages.blobUrls}
                    onBlur={validateBlobUrls}
                />
                <Grid item xs={12} md={12} padding={"0px"}>
                    <span className="display_form_labels">Total Claim Amount : </span>
                    <span style={{ fontWeight: 500 }}>{currencySymbol} {formatDecimal(formData.totalClaimAmount)}</span>
                </Grid>

                {/* Button Container */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, width: "100%" }}>
                    <Button
                        variant="outlined"
                        sx={{ color: '#23C6B1', borderColor: '#23C6B1', textTransform: 'none' }}
                        onClick={() => handleSave(true)}
                        disabled={!isFormValid()}
                    >
                        Save & Add Another
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ backgroundColor: '#23C6B1', color: '#fff', ml: 2, textTransform: 'none' }}
                        onClick={() => handleSave(false)}
                        disabled={!isFormValid()}
                    >
                        Proceed
                    </Button>
                </Box>
            </Grid >
        </Box >
    );
};

export default AssetInstallationForm;
